
// src/telemetry/Telemetry.js

// Event types (doc comment ref)
// HOME_LOADED, START_INSPECTION_CLICKED, RESTORE_IMAGE_CLICKED,
// IMAGE_CAPTURED (payload: { sequenceNo:number, docName:string }),
// UPLOAD_CLICKED, SUBMIT_CLICKED, THANK_YOU_LOADED,
// ERROR_IN_UPLOAD_MODEL, LOCATION_CAMERA_POPUP

export default class Telemetry {
  static _instance = null;

  static init({ baseUrl, tokenProvider, flushIntervalMs = 2000 }) {
    // baseUrl: e.g. http://localhost:3000/telemetry  (main proxy)
    // or      http://localhost:4000                 (direct telemetry service)
    Telemetry._instance = new Telemetry(baseUrl, tokenProvider, flushIntervalMs);
    return Telemetry._instance;
  }

  static get instance() {
    if (!Telemetry._instance) {
      throw new Error('Telemetry not initialized. Call Telemetry.init(...) first.');
    }
    return Telemetry._instance;
  }

  constructor(baseUrl, tokenProvider, flushIntervalMs) {
    this.baseUrl = (baseUrl || '').replace(/\/+$/, ''); // trim trailing slash
    this.tokenProvider = tokenProvider; // () => token | Promise<token>
    this.flushIntervalMs = flushIntervalMs;
    this.sessionId = undefined;
    this.queue = [];
    this.flushTimer = window.setInterval(() => this.flush(), this.flushIntervalMs);
    window.addEventListener('beforeunload', () => this.flush(true));
  }

  async createSession(claimNum, metadata) {
    const url = `${this.baseUrl}/v1/sessions`;
    const headers = await this._headers();
    const body = JSON.stringify({ claimNum, metadata });

    const resp = await fetch(url, { method: 'POST', headers, body });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to create session: ${resp.status} ${text}`);
    }
    const data = await resp.json();
    this.sessionId = data.id;
    return data.id;
  }

  setSessionId(sessionId) {
    this.sessionId = sessionId;
  }

  getSessionId() {
    return this.sessionId;
  }

  track(type, payload) {
    // client-side guard for IMAGE_CAPTURED shape
    if (type === 'IMAGE_CAPTURED') {
      if (!payload || typeof payload.sequenceNo !== 'number' || typeof payload.docName !== 'string' || !payload.docName) {
        console.warn('IMAGE_CAPTURED requires payload.sequenceNo:number and payload.docName:string');
      }
    }

    const idempotencyKey =
      (crypto && crypto.randomUUID && crypto.randomUUID()) ||
      `${type}-${Date.now()}-${Math.random()}`;

    this.queue.push({
      type,
      payload,
      occurredAt: new Date().toISOString(),
      idempotencyKey,
    });
  }

  async flush(sync = false) {
    if (!this.sessionId || this.queue.length === 0) return;

    const url = `${this.baseUrl}/v1/sessions/${this.sessionId}/events`;
    const batch = [...this.queue];
    this.queue = [];

    const body = JSON.stringify({ events: batch });
    try {
      const headers = await this._headers();
      if (sync && navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
      } else {
        await fetch(url, { method: 'POST', headers, body, keepalive: true });
      }
    } catch (err) {
      console.error('Telemetry flush error:', err);
      // re-queue if failed
      this.queue.unshift(...batch);
    }
  }

  dispose() {
    if (this.flushTimer) clearInterval(this.flushTimer);
    this.flush(true);
  }

  async _headers() {
    const headers = { 'Content-Type': 'application/json' };
    try {
      if (this.tokenProvider) {
        const t = this.tokenProvider();
        const token = t && typeof t.then === 'function' ? await t : t;
        if (token) headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (e) {
      // ignore token errors
    }
    return headers;
  }
}
